
import { useState, useEffect, useCallback } from "react";
import { toast } from "@/hooks/use-toast";
import { 
  USER_DASHBOARD_LAYOUT_KEY,
  ADMIN_DASHBOARD_LAYOUT_KEY,
  fetchSavedLayout,
  saveLayoutToDatabase,
  DEFAULT_USER_WIDGET_ORDER,
  LayoutItem,
  DashboardLayout
} from "@/components/dashboard/utils";
import { DEFAULT_WIDGET_ORDER } from "@/components/admin/dashboard/constants";
import { filterActiveWidgets } from "@/services/widgetManager";

export function useDashboardLayout(dashboardType = 'admin-dashboard') {
  const [handlesVisible, setHandlesVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedToDb, setSavedToDb] = useState(false);
  const [layoutInitialized, setLayoutInitialized] = useState(false);
  const [isWidgetsModalOpen, setIsWidgetsModalOpen] = useState(false);
  const [allWidgetItems, setAllWidgetItems] = useState<LayoutItem[]>([]);
  const [layoutType, setLayoutType] = useState<'grid' | 'freeform'>('grid');
  
  const layoutKey = dashboardType === 'admin-dashboard' ? 
    ADMIN_DASHBOARD_LAYOUT_KEY : USER_DASHBOARD_LAYOUT_KEY;
  
  const defaultWidgetOrder = dashboardType === 'admin-dashboard' ? 
    DEFAULT_WIDGET_ORDER : DEFAULT_USER_WIDGET_ORDER;
  
  const dashboardTypeForWidgets = dashboardType === 'admin-dashboard' ? 'admin' : 'user';
  
  useEffect(() => {
    const loadSavedLayout = async () => {
      try {
        const savedLayout = await fetchSavedLayout(dashboardType) as DashboardLayout | null;
        
        if (savedLayout) {
          console.log(`Setting initial layout from saved ${dashboardType} layout:`, savedLayout);
          
          // Set layout type if available
          if (savedLayout.type) {
            setLayoutType(savedLayout.type);
          }
          
          if (savedLayout.items && savedLayout.items.length > 0) {
            const activeLayoutItems = await filterActiveWidgets(
              savedLayout.items, 
              dashboardTypeForWidgets
            );
            
            setAllWidgetItems(activeLayoutItems);
          } else {
            console.log(`No saved ${dashboardType} items, using default order`);
            
            const activeDefaultWidgets = await filterActiveWidgets(
              defaultWidgetOrder, 
              dashboardTypeForWidgets
            );
            
            setAllWidgetItems(activeDefaultWidgets);
          }
        } else {
          console.log(`No saved ${dashboardType} layout, using default order`);
          
          const activeDefaultWidgets = await filterActiveWidgets(
            defaultWidgetOrder, 
            dashboardTypeForWidgets
          );
          
          setAllWidgetItems(activeDefaultWidgets);
        }
        setLayoutInitialized(true);
      } catch (error) {
        console.error(`Error loading saved ${dashboardType} layout:`, error);
        
        const activeDefaultWidgets = await filterActiveWidgets(
          defaultWidgetOrder, 
          dashboardTypeForWidgets
        );
        
        setAllWidgetItems(activeDefaultWidgets);
        setLayoutInitialized(true);
      }
    };

    loadSavedLayout();
  }, [dashboardType, defaultWidgetOrder, dashboardTypeForWidgets]);
  
  const resetDashboardLayout = useCallback(() => {
    localStorage.removeItem(layoutKey);
    setAllWidgetItems(defaultWidgetOrder);
    setLayoutType('grid');
    
    toast({
      title: "Layout Reset",
      description: `Your ${dashboardType === 'admin-dashboard' ? 'admin' : 'user'} dashboard has been reset to the default layout.`,
    });
  }, [layoutKey, defaultWidgetOrder, dashboardType]);

  const toggleHandlesVisibility = useCallback(() => {
    setHandlesVisible(prev => !prev);
    
    toast({
      title: handlesVisible ? "Edit Mode Deactivated" : "Edit Mode Activated",
      description: handlesVisible 
        ? "You can no longer edit the dashboard." 
        : "You can now rearrange, add, or remove widgets from the dashboard.",
      duration: 2000,
    });
  }, [handlesVisible]);

  const saveLocalLayout = useCallback(() => {
    console.log(`Saving local ${dashboardType} layout with all widgets:`, allWidgetItems);
    
    const cleanLayoutData = {
      items: allWidgetItems.map(item => ({
        id: item.id,
        size: item.size,
        position: item.position
      })),
      type: layoutType
    };
    
    localStorage.setItem(layoutKey, JSON.stringify(cleanLayoutData));
    
  }, [allWidgetItems, layoutKey, dashboardType, layoutType]);

  const handleSaveLayout = useCallback(() => {
    const layoutData = {
      items: allWidgetItems.map(item => ({
        id: item.id,
        size: item.size,
        position: item.position
      })),
      type: layoutType
    };
    
    saveLayoutToDatabase(layoutData, dashboardType, toast, setIsSaving, setSavedToDb);
  }, [allWidgetItems, dashboardType, layoutType]);
  
  const handleRemoveWidget = useCallback((widgetId: string) => {
    console.log(`Removing widget from ${dashboardType}:`, widgetId);
    
    setAllWidgetItems(prev => prev.filter(item => item.id !== widgetId));
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
    
    toast({
      title: "Widget Removed",
      description: "Widget has been removed from the dashboard. Save to persist changes.",
      duration: 2000,
    });
  }, [saveLocalLayout, dashboardType]);
  
  const handleAddWidget = useCallback(async (widgetId: string, size: 'small' | 'medium' | 'large' | 'xlarge') => {
    console.log(`Adding widget to ${dashboardType}:`, widgetId, "with size:", size);
    
    const activeWidgets = await filterActiveWidgets(
      [{ id: widgetId, size }],
      dashboardTypeForWidgets
    );
    
    if (activeWidgets.length === 0) {
      toast({
        title: "Widget Unavailable",
        description: "This widget is currently disabled or not available for this dashboard.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    // Find a position for the new widget
    let maxY = 0;
    allWidgetItems.forEach(item => {
      if (item.position && item.position.y > maxY) {
        maxY = item.position.y;
      }
    });
    
    const newItem: LayoutItem = { 
      id: widgetId, 
      size,
      position: { x: 0, y: maxY + 1 }
    };
    
    if (!allWidgetItems.some(item => item.id === widgetId)) {
      setAllWidgetItems(prev => [...prev, newItem]);
    }
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
    
    setIsWidgetsModalOpen(false);
  }, [allWidgetItems, saveLocalLayout, dashboardType, dashboardTypeForWidgets]);

  const handleWidgetsReorder = useCallback((newOrder: LayoutItem[]) => {
    console.log(`Reordering widgets in ${dashboardType}:`, newOrder);
    
    setAllWidgetItems(newOrder);
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
  }, [saveLocalLayout, dashboardType]);
  
  const updateWidgetSize = useCallback((widgetId: string, newSize: 'small' | 'medium' | 'large' | 'xlarge') => {
    console.log(`Updating widget size in ${dashboardType}:`, widgetId, "to", newSize);
    
    setAllWidgetItems(prev => 
      prev.map(item => 
        item.id === widgetId ? { ...item, size: newSize } : item
      )
    );
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
    
    toast({
      title: "Widget Resized",
      description: "Widget size has been updated. Save to persist changes.",
      duration: 2000,
    });
  }, [saveLocalLayout, dashboardType]);

  const updateWidgetPosition = useCallback((widgetId: string, position: { x: number, y: number }) => {
    console.log(`Updating widget position in ${dashboardType}:`, widgetId, "to", position);
    
    setAllWidgetItems(prev => 
      prev.map(item => 
        item.id === widgetId ? { ...item, position } : item
      )
    );
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
  }, [saveLocalLayout, dashboardType]);

  const changeLayoutType = useCallback((type: 'grid' | 'freeform') => {
    setLayoutType(type);
    
    // If switching to free-form and we don't have positions, set default positions
    if (type === 'freeform') {
      setAllWidgetItems(prev => {
        let yPosition = 0;
        return prev.map((item, index) => {
          if (!item.position) {
            const position = { 
              x: 0, 
              y: yPosition 
            };
            yPosition += item.size === 'large' || item.size === 'xlarge' ? 3 : 2;
            return { ...item, position };
          }
          return item;
        });
      });
    }
    
    toast({
      title: `Layout Type Changed`,
      description: `Dashboard layout changed to ${type === 'grid' ? 'grid layout' : 'free positioning'}.`,
      duration: 2000,
    });
    
    setTimeout(() => {
      saveLocalLayout();
    }, 100);
  }, [saveLocalLayout]);

  return {
    handlesVisible,
    isSaving,
    savedToDb,
    layoutInitialized,
    isWidgetsModalOpen,
    allWidgetItems,
    layoutType,
    setIsWidgetsModalOpen,
    toggleHandlesVisibility,
    resetDashboardLayout,
    handleSaveLayout,
    handleRemoveWidget,
    handleAddWidget,
    handleWidgetsReorder,
    updateWidgetSize,
    updateWidgetPosition,
    changeLayoutType
  };
}
